import React, { useEffect, useState, useRef } from 'react';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { useI18n } from '../context/I18nContext';
import { useSearchParams } from 'react-router-dom';
import { Mic, Settings as SettingsIcon, PhoneCall, Save, Loader, Play, Pause, CheckCircle, ChevronDown, ChevronUp, BookOpen, Plus, Trash2, RefreshCcw } from 'lucide-react';
import { formatDate } from '../utils';

const VOICES = [
  { id: 'kari', name: 'Kari', gender: 'Kvinne', description: 'Varm og profesjonell kvinnestemme' },
  { id: 'lars', name: 'Lars', gender: 'Mann', description: 'Rolig og tydelig mannsstemme' },
];

export default function AgentConfigPage() {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const validAgentTabs = ['config', 'voice', 'test', 'knowledge'];
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(validAgentTabs.includes(tabFromUrl) ? tabFromUrl : 'config');

  const handleAgentTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId }, { replace: true });
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backendAvailable, setBackendAvailable] = useState(true);
  const [form, setForm] = useState({
    greeting: '',
    transfer_number: '',
    business_name: '',
    voice: 'kari',
  });

  // Test agent
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);
  const [testMessage, setTestMessage] = useState('');

  // Voice
  const [playingVoice, setPlayingVoice] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState('kari');
  const [savingVoice, setSavingVoice] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    async function load() {
      try {
        const { data } = await api.get('/api/agent-config');
        setForm({
          greeting: data.greeting || '',
          transfer_number: data.transfer_number || '',
          business_name: data.business_name || '',
          voice: data.voice || 'kari',
        });
        setSelectedVoice(data.voice || 'kari');
      } catch (err) {
        if (err.response?.status === 404) {
          setBackendAvailable(false);
        } else {
          toast.error('Kunne ikke laste agent-konfigurasjon');
        }
      }
      // Also fetch company for default test phone
      try {
        const { data } = await api.get('/api/company').catch(() => api.get('/api/settings/company'));
        if (data?.contact_phone) setTestPhone(data.contact_phone);
      } catch { /* no-op */ }
      setLoading(false);
    }
    load();
  }, []); // eslint-disable-line

  // Norwegian +47 phone validator: accepts +47XXXXXXXX (8 digits, with optional spaces).
  // Returns null if valid or empty, error string otherwise.
  const validateNorwegianPhone = (val) => {
    if (!val || !val.trim()) return null;
    const cleaned = val.replace(/[\s-]/g, '');
    if (!/^\+47\d{8}$/.test(cleaned)) {
      return 'Telefonnummer må være på formatet +47XXXXXXXX (8 siffer etter +47)';
    }
    return null;
  };

  const handleSave = async () => {
    // Inline validation
    const phoneErr = validateNorwegianPhone(form.transfer_number);
    if (phoneErr) {
      toast.error(phoneErr);
      return;
    }
    if (form.greeting && form.greeting.length > 1000) {
      toast.error('Hilsningstekst kan ikke overstige 1000 tegn');
      return;
    }
    setSaving(true);
    try {
      await api.patch('/api/agent-config', form);
      toast.success('Konfigurasjon lagret');
    } catch (err) {
      if (err.response?.status === 404) {
        toast.info('Lagrer ikke ennå — backend ikke klar');
      } else {
        toast.error('Kunne ikke lagre konfigurasjon');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTestCall = async () => {
    if (!testPhone) {
      toast.error('Vennligst skriv inn et telefonnummer');
      return;
    }
    const phoneErr = validateNorwegianPhone(testPhone);
    if (phoneErr) {
      toast.error(phoneErr);
      return;
    }
    setTesting(true);
    setTestMessage('');
    try {
      await api.post('/api/agent-config/test-call', { phone_number: testPhone });
      setTestMessage('Din AI-resepsjonist ringer deg om noen sekunder...');
      toast.success('Testanrop startet');
    } catch (err) {
      toast.error(err.response?.status === 404 ? 'Testanrop ikke tilgjengelig ennå' : 'Kunne ikke starte testanrop');
    } finally {
      setTesting(false);
    }
  };

  const handlePlayVoice = async (voiceId) => {
    if (playingVoice === voiceId) {
      if (audioRef.current) audioRef.current.pause();
      setPlayingVoice(null);
      return;
    }
    try {
      const response = await api.get(`/api/agent-config/voice-preview?voice=${voiceId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'audio/mpeg' }));
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = url;
        audioRef.current.onended = () => {
          setPlayingVoice(null);
          URL.revokeObjectURL(url);
        };
        await audioRef.current.play();
        setPlayingVoice(voiceId);
      } else {
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      toast.error(err.response?.status === 404 ? 'Forhåndsvisning ikke tilgjengelig' : 'Kunne ikke spille av stemme');
    }
  };

  const handleSaveVoice = async () => {
    setSavingVoice(true);
    try {
      await api.patch('/api/agent-config', { voice: selectedVoice });
      setForm(f => ({ ...f, voice: selectedVoice }));
      toast.success('Stemme lagret');
    } catch (err) {
      if (err.response?.status === 404) toast.info('Lagrer ikke ennå — backend ikke klar');
      else toast.error('Kunne ikke lagre stemme');
    } finally {
      setSavingVoice(false);
    }
  };

  const tabs = [
    { id: 'config', label: 'Konfigurasjon', icon: SettingsIcon },
    { id: 'knowledge', label: 'Kunnskapsbase', icon: BookOpen },
    { id: 'test', label: 'Test agent', icon: PhoneCall },
    { id: 'voice', label: 'Stemme', icon: Mic },
  ];

  if (loading) return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 bg-navy-800 rounded w-48" />
      <div className="h-12 bg-navy-800 rounded-lg w-80" />
      <div className="h-96 bg-navy-800 rounded-lg" />
    </div>
  );

  return (
    <div data-testid="agent-config-page">
      <audio ref={audioRef} hidden />
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-white tracking-tight">Agent-konfigurasjon</h1>
        <p className="text-slate-500 text-sm mt-1">Tilpass hvordan din AI-resepsjonist oppfører seg og høres ut</p>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => handleAgentTabChange(id)}
            data-testid={`agent-tab-${id}`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === id ? 'bg-accent-blue text-white' : 'bg-navy-800 text-slate-400 border border-navy-600 hover:border-accent-blue'
            }`}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {/* Konfigurasjon tab */}
      {activeTab === 'config' && (
        <div className="space-y-5">
        {/* Persona preview */}
        {(form.greeting || form.business_name) && (
          <div className="bg-navy-800 border border-accent-blue/30 rounded-lg p-5" data-testid="agent-persona-preview">
            <div className="flex items-center gap-2 mb-3">
              <Mic size={14} className="text-accent-blue" />
              <h3 className="text-xs font-semibold tracking-widest uppercase text-accent-blue">Forhåndsvisning av AI-resepsjonist</h3>
            </div>
            <div className="bg-navy-900 rounded-lg p-4 border border-navy-700">
              <div className="text-xs text-slate-500 mb-1">Stemme: <span className="text-slate-300">{VOICES.find(v => v.id === (form.voice || selectedVoice))?.name || 'Standard'}</span></div>
              <div className="text-sm text-white italic leading-relaxed">
                "{(form.greeting || `Hei, du har kommet til ${form.business_name || '[bedrift]'}. Hvordan kan jeg hjelpe deg?`).replace(/\[bedrift\]/g, form.business_name || '[bedrift]')}"
              </div>
              {form.transfer_number && (
                <div className="mt-3 pt-3 border-t border-navy-700 text-xs text-slate-500">
                  Ved overføring til menneske: <span className="font-mono text-slate-300">{form.transfer_number}</span>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 space-y-5">
          <h3 className="font-heading text-base font-semibold text-white">Grunnleggende innstillinger</h3>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500">Hilsningstekst</label>
              <span className={`text-xs font-mono ${
                (form.greeting || '').length > 900 ? 'text-accent-red' :
                (form.greeting || '').length > 700 ? 'text-accent-yellow' : 'text-slate-600'
              }`}>{(form.greeting || '').length}/1000</span>
            </div>
            <textarea
              rows={4}
              maxLength={1000}
              value={form.greeting}
              onChange={(e) => setForm({ ...form, greeting: e.target.value })}
              placeholder="Hei, du har kommet til [bedrift]. Hvordan kan jeg hjelpe deg?"
              className={`w-full px-4 py-3 bg-navy-800 border rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 resize-none ${
                (form.greeting || '').length > 900 ? 'border-accent-red focus:ring-accent-red' : 'border-navy-600 focus:ring-accent-blue'
              }`}
            />
            {(form.greeting || '').length > 900 ? (
              <p className="text-xs text-accent-red mt-1.5">Nærmer seg grensen på 1000 tegn</p>
            ) : (
              <p className="text-xs text-slate-500 mt-1.5">Dette er det første innringere hører når de ringer.</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Videresendingsnummer</label>
            <input
              type="tel"
              value={form.transfer_number}
              onChange={(e) => setForm({ ...form, transfer_number: e.target.value })}
              placeholder="+47XXXXXXXX"
              data-testid="agent-transfer-number"
              className={`w-full px-4 py-3 bg-navy-800 border rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue font-mono ${
                form.transfer_number && validateNorwegianPhone(form.transfer_number) ? 'border-accent-red' : 'border-navy-600'
              }`}
            />
            {form.transfer_number && validateNorwegianPhone(form.transfer_number) ? (
              <p className="text-xs text-accent-red mt-1.5">{validateNorwegianPhone(form.transfer_number)}</p>
            ) : (
              <p className="text-xs text-slate-500 mt-1.5">Norsk format: +47 etterfulgt av 8 siffer (f.eks. +4712345678)</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Bedriftsnavn (som AI presenterer seg med)</label>
            <input
              type="text"
              value={form.business_name}
              onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              placeholder="Din Bedrift AS"
              className="w-full px-4 py-3 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
            />
          </div>

          {/* Preview greeting conversation — collapsible */}
          <ConversationPreview greeting={form.greeting} businessName={form.business_name} transferNumber={form.transfer_number} />

          <div className="pt-3 border-t border-navy-600">
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="agent-config-save"
              className="flex items-center gap-2 px-6 py-3 bg-accent-blue hover:bg-accent-blue-hover text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
              {saving ? 'Lagrer...' : 'Lagre endringer'}
            </button>
            {!backendAvailable && (
              <p className="text-xs text-slate-500 mt-3">Obs: Backend-endepunktet er ikke klart ennå. Endringer blir ikke lagret før det er oppe.</p>
            )}
          </div>
        </div>
        </div>
      )}

      {/* Test agent tab */}
      {activeTab === 'test' && (
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 space-y-5">
          <h3 className="font-heading text-base font-semibold text-white">Test din AI-resepsjonist</h3>
          <p className="text-sm text-slate-400">Ring deg selv for å teste agenten før du går live.</p>

          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Ring meg på</label>
            <input
              type="tel"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="+47 XXX XX XXX"
              className="w-full px-4 py-3 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue font-mono"
            />
          </div>

          <button
            onClick={handleTestCall}
            disabled={testing || !testPhone}
            data-testid="agent-test-call-button"
            className="flex items-center gap-2 px-6 py-3 bg-accent-green hover:bg-accent-green/80 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
          >
            {testing ? <Loader size={16} className="animate-spin" /> : <PhoneCall size={16} />}
            Ring meg nå
          </button>

          {testMessage && (
            <div className="mt-4 p-4 bg-accent-green/10 border border-accent-green/30 rounded-lg flex items-center gap-3">
              <CheckCircle size={18} className="text-accent-green flex-shrink-0" />
              <span className="text-sm text-accent-green">{testMessage}</span>
            </div>
          )}
        </div>
      )}

      {/* Kunnskapsbase tab */}
      {activeTab === 'knowledge' && (
        <KnowledgeBaseEditor businessName={form.business_name} />
      )}

      {/* Stemme tab */}
      {activeTab === 'voice' && (
        <div className="space-y-5">
          <div className="flex items-start gap-3 p-4 bg-navy-800 border border-navy-600 rounded-lg">
            <Mic size={18} className="text-accent-blue flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-300">
              Stemmevalg synkroniseres automatisk til AI-agenten. Det kan ta opptil 5 minutter før endringen er aktiv på nye samtaler.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {VOICES.map(v => {
              const selected = selectedVoice === v.id;
              const playing = playingVoice === v.id;
              return (
                <button
                  key={v.id}
                  onClick={() => setSelectedVoice(v.id)}
                  data-testid={`voice-option-${v.id}`}
                  className={`text-left bg-navy-800 border rounded-lg p-6 transition-all ${
                    selected ? 'border-accent-green ring-2 ring-accent-green/40' : 'border-navy-600 hover:border-accent-blue'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-heading text-lg font-bold text-white">{v.name} <span className="text-sm font-normal text-slate-500">({v.gender})</span></h3>
                      <p className="text-sm text-slate-400 mt-1">{v.description}</p>
                    </div>
                    {selected && <CheckCircle size={18} className="text-accent-green flex-shrink-0" />}
                  </div>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); handlePlayVoice(v.id); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handlePlayVoice(v.id); } }}
                    className="mt-4 inline-flex items-center gap-2 px-3 py-2 bg-navy-700 border border-navy-600 rounded-lg text-sm text-slate-300 hover:border-accent-blue hover:text-white transition-colors cursor-pointer"
                  >
                    {playing ? <Pause size={14} /> : <Play size={14} />}
                    {playing ? 'Stopp' : 'Spill av'}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="pt-3">
            <button
              onClick={handleSaveVoice}
              disabled={savingVoice}
              data-testid="voice-save-button"
              className="flex items-center gap-2 px-6 py-3 bg-accent-blue hover:bg-accent-blue-hover text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {savingVoice ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
              Lagre stemmevalg
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ConversationPreview({ greeting, businessName, transferNumber }) {
  const [open, setOpen] = useState(false);
  const bn = businessName || '[bedrift]';
  const tn = transferNumber || '+47 XX XX XX XX';
  const greetingText = (greeting || `Hei, du har kommet til ${bn}. Hvordan kan jeg hjelpe deg?`).replace(/\[bedrift\]/g, bn);

  const lines = [
    { who: 'ai', text: greetingText },
    { who: 'caller', text: 'Hei, jeg lurer på åpningstidene deres.' },
    { who: 'ai', text: `Vi er åpne mandag til fredag fra 08:00 til 17:00. Er det noe annet jeg kan hjelpe deg med?` },
    { who: 'caller', text: 'Ja, jeg vil gjerne bestille en time neste uke.' },
    { who: 'ai', text: `Det kan jeg hjelpe deg med. Hva slags type time ønsker du, og passer det best på morgenen eller ettermiddagen?` },
    { who: 'caller', text: 'Ettermiddag passer best.' },
    { who: 'ai', text: `Flott. Jeg setter deg over til en av våre medarbeidere som kan finne et tidspunkt som passer. Et øyeblikk — jeg ringer ${tn}.` },
  ];

  return (
    <div className="border border-navy-600 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-3 bg-navy-700/50 border-b border-navy-600 flex items-center justify-between hover:bg-navy-700 transition-colors"
        data-testid="conversation-preview-toggle"
      >
        <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">Se eksempel på samtale</span>
        {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>
      {open && (
        <>
          <div className="p-5 space-y-3 bg-navy-800/50">
            {lines.map((line, i) => (
              <div key={i} className={`flex gap-3 ${line.who === 'caller' ? 'justify-end' : ''}`}>
                {line.who === 'ai' && (
                  <div className="w-7 h-7 rounded-full bg-accent-blue/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-accent-blue">AI</span>
                  </div>
                )}
                <div className={`rounded-lg px-4 py-2.5 text-sm text-white max-w-[80%] ${line.who === 'ai' ? 'bg-navy-700/60' : 'bg-accent-blue/15 border border-accent-blue/20'}`}>
                  {line.text}
                </div>
                {line.who === 'caller' && (
                  <div className="w-7 h-7 rounded-full bg-slate-600/50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs text-slate-400">👤</span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 px-5 py-3 border-t border-navy-600 bg-navy-700/30">Simulert samtale — faktiske svar tilpasses din konfigurasjon, åpningstider og kunnskapsbase.</p>
        </>
      )}
    </div>
  );
}

// ==============================
// Knowledge Base editor — structured FAQ + services + important info
// ==============================
const KB_LIMITS = { services: 10, faqs: 15, important_info: 20 };

function KnowledgeBaseEditor({ businessName }) {
  const toast = useToast();
  const { t } = useI18n();
  const [services, setServices] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [importantInfo, setImportantInfo] = useState([]);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const { data } = await api.get('/api/agent-config/knowledge-base');
        if (!mounted) return;
        setServices(data.services || []);
        setFaqs(data.faqs || []);
        setImportantInfo(data.important_info || []);
        setLastSyncedAt(data.last_synced_at || null);
      } catch (err) {
        if (mounted && err.response?.status !== 404) {
          toast.error(t('kb_load_error', 'Kunne ikke laste kunnskapsbase'));
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const markDirty = () => setDirty(true);

  // Services
  const addService = () => {
    if (services.length >= KB_LIMITS.services) {
      toast.info(`${t('kb_max', 'Maks')} ${KB_LIMITS.services} ${t('kb_services_word', 'tjenester')}`);
      return;
    }
    setServices([...services, { name: '', description: '' }]);
    markDirty();
  };
  const updateService = (i, key, value) => {
    setServices(services.map((s, idx) => idx === i ? { ...s, [key]: value } : s));
    markDirty();
  };
  const removeService = (i) => {
    setServices(services.filter((_, idx) => idx !== i));
    markDirty();
  };

  // FAQs
  const addFaq = () => {
    if (faqs.length >= KB_LIMITS.faqs) {
      toast.info(`${t('kb_max', 'Maks')} ${KB_LIMITS.faqs} ${t('kb_questions_word', 'spørsmål')}`);
      return;
    }
    setFaqs([...faqs, { question: '', answer: '' }]);
    markDirty();
  };
  const updateFaq = (i, key, value) => {
    setFaqs(faqs.map((f, idx) => idx === i ? { ...f, [key]: value } : f));
    markDirty();
  };
  const removeFaq = (i) => {
    setFaqs(faqs.filter((_, idx) => idx !== i));
    markDirty();
  };

  // Important info
  const addInfo = () => {
    if (importantInfo.length >= KB_LIMITS.important_info) {
      toast.info(`${t('kb_max', 'Maks')} ${KB_LIMITS.important_info} ${t('kb_info_fields_word', 'info-felter')}`);
      return;
    }
    setImportantInfo([...importantInfo, { key: '', value: '' }]);
    markDirty();
  };
  const updateInfo = (i, key, value) => {
    setImportantInfo(importantInfo.map((kv, idx) => idx === i ? { ...kv, [key]: value } : kv));
    markDirty();
  };
  const removeInfo = (i) => {
    setImportantInfo(importantInfo.filter((_, idx) => idx !== i));
    markDirty();
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/api/agent-config/knowledge-base', {
        services, faqs, important_info: importantInfo,
      });
      setLastSyncedAt(data.last_synced_at || new Date().toISOString());
      setDirty(false);
      if (data.retell_updated) {
        toast.success(t('kb_saved_synced', 'Kunnskapsbase lagret og synkronisert til AI-agenten'));
      } else if (data.retell_error) {
        toast.info(t('kb_saved_sync_failed', 'Lagret, men sync til AI-agent feilet — prøv igjen senere'));
      } else {
        toast.success(t('kb_saved', 'Kunnskapsbase lagret'));
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || t('kb_save_error', 'Kunne ikke lagre kunnskapsbase'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse" data-testid="kb-loading">
        <div className="h-32 bg-navy-800 rounded-lg" />
        <div className="h-48 bg-navy-800 rounded-lg" />
        <div className="h-32 bg-navy-800 rounded-lg" />
      </div>
    );
  }

  const previewText = buildKBPreview({ services, faqs, importantInfo, businessName });

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5" data-testid="kb-editor">
      {/* Left column — editor */}
      <div className="xl:col-span-2 space-y-5">
        {/* Header */}
        <div className="flex items-start gap-3 p-4 bg-navy-800 border border-navy-600 rounded-lg">
          <BookOpen size={18} className="text-accent-blue flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-slate-300">
              {t('kb_intro', 'Strukturert kunnskap som AI-agenten bruker når den svarer innringere. Endringer synkroniseres til agenten når du lagrer.')}
            </p>
          </div>
        </div>

        {/* Section A — Services */}
        <KBSection
          icon={SettingsIcon}
          title={t('kb_services_title', 'Tjenester')}
          subtitle={`${t('kb_services_subtitle', 'Hva tilbyr bedriften?')} (${t('kb_max_lower', 'maks')} ${KB_LIMITS.services})`}
          count={services.length}
          limit={KB_LIMITS.services}
          onAdd={addService}
          addLabel={t('kb_add_service', 'Legg til tjeneste')}
          testId="kb-services"
        >
          {services.length === 0 ? (
            <p className="text-xs text-slate-500 italic px-1">{t('kb_no_services', 'Ingen tjenester lagt til ennå.')}</p>
          ) : services.map((s, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr,2fr,auto] gap-2 items-start" data-testid={`kb-service-row-${i}`}>
              <input
                type="text"
                value={s.name}
                onChange={(e) => updateService(i, 'name', e.target.value)}
                placeholder={t('kb_service_name_ph', 'Tjenestenavn')}
                maxLength={120}
                className="px-3 py-2 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
              />
              <input
                type="text"
                value={s.description}
                onChange={(e) => updateService(i, 'description', e.target.value)}
                placeholder={t('kb_service_desc_ph', 'Kort beskrivelse')}
                maxLength={500}
                className="px-3 py-2 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
              />
              <button
                onClick={() => removeService(i)}
                className="p-2 text-slate-500 hover:text-accent-red rounded-lg hover:bg-navy-700 transition-colors"
                title={t('kb_remove', 'Fjern')}
                aria-label={t('kb_remove_entry_aria', 'Fjern oppføring')}
                data-testid={`kb-service-remove-${i}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </KBSection>

        {/* Section B — FAQs */}
        <KBSection
          icon={BookOpen}
          title={t('kb_faqs_title', 'Vanlige spørsmål')}
          subtitle={`${t('kb_faqs_subtitle', 'Spørsmål og svar')} (${t('kb_max_lower', 'maks')} ${KB_LIMITS.faqs})`}
          count={faqs.length}
          limit={KB_LIMITS.faqs}
          onAdd={addFaq}
          addLabel={t('kb_add_qa', 'Legg til Q&A')}
          testId="kb-faqs"
        >
          {faqs.length === 0 ? (
            <p className="text-xs text-slate-500 italic px-1">{t('kb_no_faqs', 'Ingen spørsmål lagt til ennå.')}</p>
          ) : faqs.map((f, i) => (
            <div key={i} className="space-y-2 p-3 bg-navy-900/50 border border-navy-700 rounded-lg" data-testid={`kb-faq-row-${i}`}>
              <div className="flex items-start gap-2">
                <input
                  type="text"
                  value={f.question}
                  onChange={(e) => updateFaq(i, 'question', e.target.value)}
                  placeholder={t('kb_question_ph', 'Spørsmål')}
                  maxLength={200}
                  className="flex-1 px-3 py-2 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
                />
                <button
                  onClick={() => removeFaq(i)}
                  className="p-2 text-slate-500 hover:text-accent-red rounded-lg hover:bg-navy-700 transition-colors"
                  title={t('kb_remove', 'Fjern')}
                  aria-label={t('kb_remove_entry_aria', 'Fjern oppføring')}
                  data-testid={`kb-faq-remove-${i}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <textarea
                value={f.answer}
                onChange={(e) => updateFaq(i, 'answer', e.target.value)}
                placeholder={t('kb_answer_ph', 'Svar')}
                maxLength={1000}
                rows={2}
                className="w-full px-3 py-2 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue resize-none"
              />
            </div>
          ))}
        </KBSection>

        {/* Section C — Important info */}
        <KBSection
          icon={SettingsIcon}
          title={t('kb_info_title', 'Viktig info')}
          subtitle={`${t('kb_info_subtitle', 'Adresse, parkering, betaling osv.')} (${t('kb_max_lower', 'maks')} ${KB_LIMITS.important_info})`}
          count={importantInfo.length}
          limit={KB_LIMITS.important_info}
          onAdd={addInfo}
          addLabel={t('kb_add_info', 'Legg til info')}
          testId="kb-info"
        >
          {importantInfo.length === 0 ? (
            <p className="text-xs text-slate-500 italic px-1">{t('kb_no_info', 'Ingen info-felter lagt til ennå.')}</p>
          ) : importantInfo.map((kv, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr,2fr,auto] gap-2 items-start" data-testid={`kb-info-row-${i}`}>
              <input
                type="text"
                value={kv.key}
                onChange={(e) => updateInfo(i, 'key', e.target.value)}
                placeholder={t('kb_info_key_ph', 'F.eks. Adresse')}
                maxLength={80}
                className="px-3 py-2 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
              />
              <input
                type="text"
                value={kv.value}
                onChange={(e) => updateInfo(i, 'value', e.target.value)}
                placeholder={t('kb_info_value_ph', 'Verdi')}
                maxLength={500}
                className="px-3 py-2 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
              />
              <button
                onClick={() => removeInfo(i)}
                className="p-2 text-slate-500 hover:text-accent-red rounded-lg hover:bg-navy-700 transition-colors"
                title={t('kb_remove', 'Fjern')}
                aria-label={t('kb_remove_entry_aria', 'Fjern oppføring')}
                data-testid={`kb-info-remove-${i}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </KBSection>
      </div>

      {/* Right column — preview + sync */}
      <div className="xl:col-span-1">
        <div className="sticky top-4 bg-navy-800 border border-navy-600 rounded-lg p-5 space-y-4">
          <div className="flex items-center gap-2">
            <RefreshCcw size={14} className="text-accent-blue" />
            <h3 className="font-heading text-sm font-semibold text-white">{t('kb_preview', 'Forhåndsvisning')}</h3>
          </div>
          <div className="bg-navy-900 border border-navy-700 rounded-lg p-3 max-h-[400px] overflow-y-auto">
            <pre className="text-xs text-slate-300 whitespace-pre-wrap font-mono leading-relaxed" data-testid="kb-preview">
{previewText || t('kb_nothing_to_preview', 'Ingenting å forhåndsvise ennå.')}
            </pre>
          </div>
          <div className="text-xs text-slate-500">
            {lastSyncedAt
              ? <>{t('kb_last_synced', 'Sist synkronisert:')} <span className="text-slate-300">{formatDate(lastSyncedAt)}</span></>
              : <>{t('kb_not_synced', 'Ikke synkronisert ennå')}</>}
          </div>
          {dirty && (
            <div className="text-xs text-accent-yellow flex items-center gap-1.5">
              ● {t('kb_unsaved_changes', 'Ulagrede endringer')}
            </div>
          )}
          <button
            onClick={save}
            disabled={saving}
            data-testid="kb-save-button"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-accent-blue hover:bg-accent-blue-hover text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? t('kb_saving', 'Lagrer...') : t('kb_save_button', 'Lagre og synkroniser')}
          </button>
        </div>
      </div>
    </div>
  );
}

function KBSection({ icon: Icon, title, subtitle, count, limit, onAdd, addLabel, children, testId }) {
  return (
    <div className="bg-navy-800 border border-navy-600 rounded-lg p-5 space-y-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            {Icon && <Icon size={14} className="text-accent-blue" />}
            <h3 className="font-heading text-base font-semibold text-white">{title}</h3>
            <span className="text-[10px] font-mono text-slate-500">{count}/{limit}</span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
        </div>
        <button
          onClick={onAdd}
          disabled={count >= limit}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-navy-700 border border-navy-600 text-slate-300 text-xs rounded-lg hover:border-accent-blue hover:text-white transition-colors disabled:opacity-50"
          data-testid={`${testId}-add`}
        >
          <Plus size={12} /> {addLabel}
        </button>
      </div>
      <div className="space-y-2">
        {children}
      </div>
    </div>
  );
}

function buildKBPreview({ services, faqs, importantInfo, businessName }) {
  const parts = [];
  const hasAny = (services?.length || 0) + (faqs?.length || 0) + (importantInfo?.length || 0) > 0;
  if (!hasAny) return '';
  parts.push(`Kunnskapsbase for ${businessName || 'bedriften'}:\n`);
  if (services?.length) {
    parts.push('--- TJENESTER ---');
    services.forEach(s => {
      const name = (s.name || '').trim();
      const desc = (s.description || '').trim();
      if (name && desc) parts.push(`- ${name}: ${desc}`);
      else if (name) parts.push(`- ${name}`);
    });
    parts.push('');
  }
  if (faqs?.length) {
    parts.push('--- VANLIGE SPØRSMÅL ---');
    faqs.forEach(f => {
      const q = (f.question || '').trim();
      const a = (f.answer || '').trim();
      if (q) parts.push(`Sp: ${q}`);
      if (a) parts.push(`Sv: ${a}`);
    });
    parts.push('');
  }
  if (importantInfo?.length) {
    parts.push('--- VIKTIG INFO ---');
    importantInfo.forEach(kv => {
      const k = (kv.key || '').trim();
      const v = (kv.value || '').trim();
      if (k && v) parts.push(`- ${k}: ${v}`);
      else if (v) parts.push(`- ${v}`);
    });
  }
  return parts.join('\n').trim();
}
